import { Chat, ChatAttachment } from '@src/api/shared/community/chatApiClient';
import { brandColors } from '@src/assets/colors/brandColors';
import SvgIconDownload from '@src/assets/images/common/icon/chat-action/SvgIconDownload';
import CloseIcon from '@src/assets/images/common/icon/close.svg';
import { useSharedAuth } from '@src/context/auth/useSharedAuth';
import AttachmentErrorModal from '@src/features/shared/community/components/chat/chatList/AttachmentErrorModal';
import { TOOLTIP_DISPLAY_TIME } from '@src/features/shared/community/components/chat/chatList/ChatItemHeader';
import Tooltip from '@src/features/shared/community/components/chat/Tooltip';
import { calcPreviewImageInitialScale } from '@src/features/shared/community/constants/previewImageScale';
import useFileDownload from '@src/hooks/useFileDownload';
import useImageSize from '@src/hooks/useImageSize';
import useTooltip from '@src/hooks/useTooltip';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';

type PreviewImageModalProps = {
  attachment: ChatAttachment;
  chat?: Chat;
  onCancel: () => void;
  isDraft?: boolean;
  imageUrl?: string;
};

// 画像ファイルのプレビューのモーダル
const PreviewImageModal = ({ attachment, onCancel, isDraft = false, imageUrl }: PreviewImageModalProps) => {
  const { showTooltip, displayTooltip } = useTooltip();
  const [attachmentErrorModalOpen, setAttachmentErrorModalOpen] = useState(false);
  const [attachmentErrorMessage, setAttachmentErrorMessage] = useState('');
  const [hasImageLoadError, setHasImageLoadError] = useState(false);

  const [initialScale, setInitialScale] = useState(1);

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = e.currentTarget;
    setInitialScale(calcPreviewImageInitialScale(naturalWidth, naturalHeight));
  };

  const imgSizeKB = useImageSize(attachment.file_url ?? '', attachment.blob);
  const { downloadFile } = useFileDownload();
  const { profile } = useSharedAuth();
  const isLoggedIn = profile?.companyProfile?.isLogin ?? profile?.tenantProfile?.isLogin;

  // @ts-expect-error define global variables for iOS detection
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  const handleDownloadClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isDraft) {
      return;
    }

    if (hasImageLoadError) {
      setAttachmentErrorMessage('ダウンロードに失敗しました。再試行してください');
      setAttachmentErrorModalOpen(true);
      return;
    }
    try {
      if (!isLoggedIn) {
        displayTooltip?.(TOOLTIP_DISPLAY_TIME);
        return;
      }
      await downloadFile(attachment.file_url, attachment.file_name);
    } catch {
      setAttachmentErrorMessage('ダウンロードに失敗しました。再試行してください。');
      setAttachmentErrorModalOpen(true);
    }
  };

  const FunctionButton = ({ onClick, label }: { onClick: () => void; label: string }) => {
    return (
      <button
        type='button'
        onClick={onClick}
        className='rounded-[4px] border border-solid border-gray-200 bg-white p-3 py-1 text-xs'
      >
        {label}
      </button>
    );
  };

  if (hasImageLoadError) {
    return (
      <AttachmentErrorModal
        fileName={attachment.file_name}
        attachmentErrorMessage={attachmentErrorMessage || 'ファイルが見つかりません'}
        onClose={onCancel}
      />
    );
  }

  return createPortal(
    <div className='chat-attachment-list relative z-[9999]'>
      <div className='fixed inset-0 z-40 bg-black opacity-30'></div>
      <div className='fixed inset-0 z-50 grid place-items-center'>
        <div className='relative h-[600px] max-h-[600px] w-[800px] rounded bg-white p-6 shadow-[0_0_8px_rgba(0,0,0,0.25)] xl:h-[570px] lg:h-[72vh] lg:w-[92vw]'>
          <p className='truncate border-b border-solid border-b-gray-350 pb-4 text-[20px] font-semibold leading-loose'>
            {attachment.file_name}
          </p>
          {/* calcの計算：100% - ヘッダーファイル名 - フッターサイズとダウンロード - ヘッダーと画像間の余白 - フッターと画像間の余白 */}
          <div className='mt-4 h-[calc(100%-47px-40px-16px-16px)] lg:h-[calc(100%-126px)]'>
            <TransformWrapper
              key={initialScale}
              initialScale={initialScale}
              centerOnInit
            >
              {({ zoomIn, zoomOut, centerView }) => (
                <div className='relative size-full'>
                  <div className='absolute left-[6px] right-[12px] top-[6px] z-10 flex flex-wrap gap-1 lg:left-[8px]'>
                    <FunctionButton
                      onClick={() => zoomIn()}
                      label='拡大'
                    />
                    <FunctionButton
                      onClick={() => zoomOut()}
                      label='縮小'
                    />
                    <FunctionButton
                      onClick={() => centerView(initialScale)}
                      label='リセット'
                    />
                    <FunctionButton
                      onClick={() => centerView()}
                      label='中央配置'
                    />
                  </div>
                  <TransformComponent wrapperClass='!size-full'>
                    <img
                      src={imageUrl ?? attachment.thumbnail_url ?? attachment.file_url}
                      alt=''
                      className={isDraft ? 'pointer-events-none select-none' : ''}
                      style={
                        isDraft
                          ? {
                              userSelect: 'none',
                              pointerEvents: 'none',
                              WebkitUserSelect: 'none',
                              MozUserSelect: 'none',
                              msUserSelect: 'none',
                            }
                          : undefined
                      }
                      onError={() => setHasImageLoadError(true)}
                      onLoad={handleImageLoad}
                      onContextMenu={isDraft ? (e) => e.preventDefault() : undefined}
                      draggable={!isDraft}
                    />
                  </TransformComponent>
                </div>
              )}
            </TransformWrapper>
          </div>
          <div className='mt-4 flex h-10 w-full items-center gap-4 bg-gray-50 px-4 py-2 lg:gap-3'>
            <div className='text-xs'>
              サイズ：{imgSizeKB !== null ? `${imgSizeKB}KB` : '取得中...'}
            </div>
            {!isDraft && (
              <>
                {isIos && attachment.file_url ? (
                  <a
                    href={isLoggedIn ? attachment.file_url : undefined}
                    download={attachment.file_name}
                    className='relative flex items-center gap-1 text-xs text-primary-500'
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isLoggedIn) {
                        displayTooltip?.(TOOLTIP_DISPLAY_TIME);
                        return;
                      }
                    }}
                  >
                    <SvgIconDownload
                      color={brandColors['500']}
                      style={{
                        width: '24px',
                        height: '24px',
                      }}
                    />
                    ダウンロード
                    {showTooltip && (
                      <div className='absolute left-1/2 top-[-45px] z-[99999] w-[290px] -translate-x-1/2 text-[12px] text-black'>
                        <Tooltip
                          label='ダウンロードするには、ログインしてください。'
                          weight='500'
                        />
                      </div>
                    )}
                  </a>
                ) : (
                  <button
                    className='relative flex items-center gap-1 text-xs text-primary-500'
                    onClick={handleDownloadClick}
                  >
                    <SvgIconDownload
                      color={brandColors['500']}
                      style={{
                        width: '24px',
                        height: '24px',
                      }}
                    />
                    ダウンロード
                    {showTooltip && (
                      <div className='absolute left-1/2 top-[-45px] z-[99999] w-[290px] -translate-x-1/2 text-[12px] text-black'>
                        <Tooltip
                          label='ダウンロードするには、ログインしてください。'
                          weight='500'
                        />
                      </div>
                    )}
                  </button>
                )}
              </>
            )}
          </div>
          <button
            className='absolute right-[8px] top-[8px] grid size-5 place-items-center rounded-full bg-gray-50 lg:right-3 lg:top-[10px]'
            onClick={onCancel}
          >
            <img
              src={CloseIcon}
              alt=''
              width={16}
              height={16}
            />
          </button>
        </div>
      </div>
      {attachmentErrorModalOpen && (
        <AttachmentErrorModal
          fileName={attachment.file_name}
          attachmentErrorMessage={attachmentErrorMessage}
          onClose={() => setAttachmentErrorModalOpen(false)}
        />
      )}
    </div>,
    document.getElementById('modal-root') || document.body,
  );
};

export default PreviewImageModal;
